import { FC, useState, useEffect, useRef, Fragment } from 'react';
import { ArrowUpToLineIcon, ArrowDownIcon, FilterIcon } from 'lucide-react';

import { Post } from './Post';
import { NbButton, NbChip } from '@/components/Neobrut';
import { useSidebar } from '../ui/sidebar';
import { useFilters } from '@/hooks/FiltersContext';
import { useFetchPets } from '@/hooks/useFetchPets';

interface PostsProps {
  status: PetStatus;
  initialData?: {
    results: Array<Pet>;
    next: string | null;
  };
}

const CONFIG: Record<
  PetStatus,
  { title: string; slogan: string; fillClass: string; pathLabel: string }
> = {
  lost: {
    title: 'Perdidos',
    slogan: 'Alguien los está buscando',
    fillClass: 'fill-lost',
    pathLabel: '/ lost',
  },
  found: {
    title: 'Encontrados',
    slogan: '¿Conocés a alguno?',
    fillClass: 'fill-found',
    pathLabel: '/ found',
  },
};

export const Posts: FC<PostsProps> = ({ status, initialData }) => {
  const titleRef = useRef<HTMLDivElement | null>(null);
  const { toggleSidebar } = useSidebar();
  const { filters, updateFilters } = useFilters();

  const {
    data: pets,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFetchPets(status, initialData);

  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      const yPosition = e.touches[0].clientY;
      setShowScrollButton(yPosition < window.innerHeight * 0.8);
    };
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    return () => window.removeEventListener('touchmove', handleTouchMove);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  const cfg = CONFIG[status];
  const totalCount = pets?.pages?.[0]
    ? ('count' in pets.pages[0] ? (pets.pages[0] as { count?: number }).count : undefined) ??
      pets.pages.reduce((acc, p) => acc + (p.results?.length ?? 0), 0)
    : 0;
  const activeFilterCount = filters ? Object.keys(filters).length : 0;

  const typeFilter = filters?.type;
  const genderFilter = filters?.gender;
  const hasScopeFilter = Boolean(typeFilter) || Boolean(genderFilter);

  const toggleFilter = <K extends 'type' | 'gender'>(key: K, value: NonNullable<Filters[K]>) => {
    const next = { ...(filters ?? {}) } as Filters;
    if (next[key] === value) {
      next[key] = undefined;
    } else {
      (next[key] as Filters[K]) = value;
    }
    updateFilters(next);
  };

  const clearScopeFilters = () => {
    updateFilters({
      ...(filters ?? {}),
      type: undefined,
      gender: undefined,
    });
  };

  return (
    <div className="mx-auto w-full max-w-4xl lg:max-w-5xl">
      {/* Colored header strip */}
      <div
        ref={titleRef}
        className={`${cfg.fillClass} relative ml-[calc(50%-50vw)] w-screen px-[calc(50vw-50%+1rem)] pb-5 pt-3`}
        style={{ borderBottom: '3px solid var(--nb-ink)' }}
      >
        <div
          className="text-[11px] font-bold uppercase tracking-[0.08em] opacity-70"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          {cfg.pathLabel}
        </div>
        <div className="mt-0.5 flex flex-wrap items-end gap-x-3 gap-y-1">
          <h1 className="font-display min-w-0 break-words text-[clamp(28px,8vw,44px)] leading-[0.9]">
            {cfg.title}
          </h1>
          <div
            className="font-display fill-ink mb-1 px-2.5 py-0.5 text-[18px] sm:text-[22px]"
            style={{ color: 'var(--nb-paper)' }}
          >
            {totalCount}
          </div>
        </div>
        <div className="mt-1.5 text-[13px] font-semibold">{cfg.slogan}</div>
      </div>

      {/* Filter chip bar */}
      <div
        className="nb-scroll-x relative ml-[calc(50%-50vw)] flex w-screen gap-2 overflow-x-auto px-[calc(50vw-50%+1rem)] py-2.5"
        style={{
          background: 'var(--nb-paper)',
          borderBottom: '3px solid var(--nb-ink)',
        }}
      >
        <NbButton fill="ink" size="sm" className="shrink-0" onClick={toggleSidebar}>
          <FilterIcon size={12} />
          filtrar {activeFilterCount > 0 && `(${activeFilterCount})`}
        </NbButton>
        <FilterPill label="Todos" active={!hasScopeFilter} onClick={clearScopeFilters} />
        <FilterPill
          label="Perros"
          active={typeFilter === 'dog'}
          onClick={() => toggleFilter('type', 'dog')}
        />
        <FilterPill
          label="Gatos"
          active={typeFilter === 'cat'}
          onClick={() => toggleFilter('type', 'cat')}
        />
        <FilterPill
          label="Macho"
          active={genderFilter === 'm'}
          onClick={() => toggleFilter('gender', 'm')}
        />
        <FilterPill
          label="Hembra"
          active={genderFilter === 'f'}
          onClick={() => toggleFilter('gender', 'f')}
        />
      </div>

      {/* Grid */}
      {isError && <div className="px-4 py-16 text-center">Error: {error?.message}</div>}
      {pets?.pages[0]?.results.length === 0 && (
        <div className="px-4 py-16 text-center">Aún no hay publicaciones...</div>
      )}
      <div className="grid grid-cols-2 gap-3 px-4 pt-4 sm:grid-cols-3 lg:grid-cols-4">
        {pets?.pages.map((page, pageIndex) => (
          <Fragment key={pageIndex}>
            {page.results.map((pet) => (
              <Post key={pet.id} pet={pet} refetch={refetch} />
            ))}
          </Fragment>
        ))}
      </div>

      {hasNextPage && (
        <div className="px-4 pt-5 text-center">
          <NbButton fill="paper" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? 'Cargando más…' : 'Cargar más'}
            <ArrowDownIcon size={14} />
          </NbButton>
        </div>
      )}

      {showScrollButton && (
        <button
          onClick={scrollToTop}
          aria-label="Volver al inicio"
          className="nb-btn fill-urgent fixed bottom-20 right-3 z-20 px-2 py-2"
        >
          <ArrowUpToLineIcon color="black" />
        </button>
      )}
    </div>
  );
};

const FilterPill: FC<{ label: string; active?: boolean; onClick: () => void }> = ({
  label,
  active,
  onClick,
}) => (
  <button type="button" onClick={onClick} className="shrink-0">
    <NbChip active={active}>{label}</NbChip>
  </button>
);
